// src/components/landing/Hero.jsx
import React from 'react';
import { useReveal } from '../../utils/reveal';

// ---- the hero drawing --------------------------------------------------------
// A lightweight SVG rather than the three.js scene: it paints on first load with
// no WebGL context, and the prompt's own preference was load speed. The drawing
// is what the product does in one picture — a plot boundary, the setback ring
// inside it, and a massing rising within the ring.
//
// Every animation here is transform or opacity. The boundary "draws itself" by
// scaling each edge up from its own start point (transform-box: view-box), not by
// animating stroke-dashoffset. And every element's resting style is its FINAL
// state, so reduced motion, or a browser that skips the animation, shows the
// finished drawing rather than an empty frame.

const K = 13;          // plan unit -> px
const OX = 240;        // projection origin
const OY = 150;
const STOREY = 20;     // px per floor
const FLOORS = 5;

// 30-degree axonometric: the view an architect sketches a massing in.
const iso = (x, y, z = 0) => [OX + (x - y) * 0.866 * K, OY + (x + y) * 0.5 * K - z];
const toPts = (poly, z = 0) =>
  poly.map(([x, y]) => iso(x, y, z).map((n) => n.toFixed(1)).join(',')).join(' ');

const PLOT = [[0, 0], [14, 0], [14, 9], [10, 12], [0, 12]];
const SETBACK = [[2, 2], [12, 2], [12, 8.2], [9.2, 10], [2, 10]];
const FOOT = [[3.2, 3.2], [10.8, 3.2], [10.8, 8.6], [3.2, 8.6]];

function HeroVisual() {
  const grid = [];
  for (let v = -2; v <= 16; v += 2) {
    const [ax, ay] = iso(v, -2);
    const [bx, by] = iso(v, 14);
    grid.push(<line key={`gx${v}`} x1={ax} y1={ay} x2={bx} y2={by} />);
  }
  for (let v = -2; v <= 14; v += 2) {
    const [ax, ay] = iso(-2, v);
    const [bx, by] = iso(16, v);
    grid.push(<line key={`gy${v}`} x1={ax} y1={ay} x2={bx} y2={by} />);
  }

  const edges = PLOT.map((p, i) => {
    const q = PLOT[(i + 1) % PLOT.length];
    const [x1, y1] = iso(p[0], p[1]);
    const [x2, y2] = iso(q[0], q[1]);
    return (
      <line
        key={`e${i}`} className="lp-edge" x1={x1} y1={y1} x2={x2} y2={y2}
        style={{ '--i': i, transformOrigin: `${x1}px ${y1}px` }}
      />
    );
  });

  const storeys = Array.from({ length: FLOORS }, (_, f) => {
    const zb = f * STOREY;
    const zt = (f + 1) * STOREY;
    return (
      <g key={`s${f}`} className="lp-storey" style={{ '--i': f }}>
        {f === 0 && <polygon className="lp-slab" points={toPts(FOOT, 0)} />}
        <polygon className={f === FLOORS - 1 ? 'lp-slab lp-roof' : 'lp-slab'} points={toPts(FOOT, zt)} />
        {FOOT.map(([x, y], c) => {
          const [ax, ay] = iso(x, y, zb);
          const [bx, by] = iso(x, y, zt);
          // corner 0 has the smallest x + y, so it sits at the back of the view
          return (
            <line key={c} className={c === 0 ? 'lp-post lp-post--back' : 'lp-post'}
                  x1={ax} y1={ay} x2={bx} y2={by} />
          );
        })}
      </g>
    );
  });

  // Plan north is -y, which this projection draws pointing up and to the right —
  // so the arrow is turned to match rather than pointing at the top of the frame.
  const nx = 60;
  const ny = 72;

  return (
    <svg className="lp-hero-svg" viewBox="0 0 480 380" aria-hidden="true" focusable="false">
      <g className="lp-grid-lines">{grid}</g>
      <polygon className="lp-plot-fill" points={toPts(PLOT)} />
      <g className="lp-plot-edges">{edges}</g>
      <polygon className="lp-setback" points={toPts(SETBACK)} />
      <g className="lp-massing">{storeys}</g>
      <g className="lp-north">
        <g transform={`translate(${nx} ${ny}) rotate(60)`}>
          <line x1="0" y1="14" x2="0" y2="-12" />
          <path d="M0 -19 4.5 -9 -4.5 -9z" />
        </g>
        <text x={nx} y={ny + 34} textAnchor="middle">N</text>
      </g>
    </svg>
  );
}

export default function Hero({ onRequestAccess, onSeeHow }) {
  const textRef = useReveal();
  return (
    <section className="lp-hero" aria-labelledby="lp-hero-title">
      <div className="lp-wrap lp-hero-grid">
        <div ref={textRef} className="lp-hero-text lp-stagger">
          <h1 id="lp-hero-title" className="lp-h1 lp-child" style={{ '--i': 0 }}>
            From a plot on the map to a compliance-checked building — before you draw a line.
          </h1>
          <p className="lp-lead lp-child" style={{ '--i': 1 }}>
            VAUOM takes a site from map to massing, reads the drawings you already have into
            measurable geometry, and checks it against the governing regulation, the climate,
            the cost and the Vastu — one continuous workflow, built for how India actually builds.
          </p>
          <div className="lp-hero-cta lp-child" style={{ '--i': 2 }}>
            <button type="button" className="btn primary lp-btn-primary lp-btn-lg" onClick={onRequestAccess}>
              Request access
            </button>
            <button type="button" className="btn lp-btn-lg" onClick={onSeeHow}>
              See how it works
            </button>
          </div>
        </div>
        <div className="lp-hero-visual">
          <HeroVisual />
        </div>
      </div>
    </section>
  );
}
